import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, IconButton, Tooltip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack,
  Checkbox, FormControlLabel, FormGroup, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { adminClient } from './data/adminClient';

const EMPTY_FORM = { email: '', name: '', judge: false, accountAdmin: false, eventAccess: '', tabulator: false, workshopAccess: '' };
const EMAIL_RE = /\S+@\S+\.\S+/;

// Privilege columns, in table order. Event Admin and Workshops are
// Edit / View / — dropdowns (ACCESS_HINTS, EventAccessSelect); the rest are
// checkboxes (PRIVILEGE_FIELDS).
const PRIVILEGE_COLUMNS = [
  { key: 'accountAdmin', label: 'Account Admin' },
  { key: 'eventAccess', label: 'Event Admin' },
  { key: 'workshopAccess', label: 'Workshops' },
  { key: 'tabulator', label: 'Tabulator' },
  { key: 'judge', label: 'Judge' },
];

// Edit = change it; View = see it without changing anything; '' = none.
// Stored in the Users tab's Event Admin / Workshops columns as TRUE / VIEW /
// blank. Event Admin covers Workshops too — the Workshops column is for
// people who should reach the workshop pages and nothing else.
const EVENT_ACCESS_OPTIONS = [
  { value: '', label: '—' },
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
];
const ACCESS_HINTS = {
  eventAccess: 'Edit: manage Event Management (including Workshops). View: see it without making changes (no School Payments).',
  workshopAccess: 'Workshops only — Edit: manage the workshop pages. View: see them without making changes. Event Admins already have this.',
};
const PRIVILEGE_FIELDS = PRIVILEGE_COLUMNS.filter(c => !ACCESS_HINTS[c.key]);
const ACCESS_FIELDS = PRIVILEGE_COLUMNS.filter(c => ACCESS_HINTS[c.key]);

function EventAccessSelect({ value, onChange, label }) {
  return (
    <TextField select size="small" label={label} value={value || ''} onChange={e => onChange(e.target.value)}
      sx={{ minWidth: label ? 160 : 90 }} SelectProps={{ displayEmpty: true }} InputLabelProps={label ? { shrink: true } : undefined}>
      {EVENT_ACCESS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
    </TextField>
  );
}

const eventAccessLabel = (value) => EVENT_ACCESS_OPTIONS.find(o => o.value === value)?.label || '—';

// Shared by both the inline-edit row and the Add dialog — Users has 6 fields
// per row (unlike Prices' single value), so both forms reuse this instead of
// duplicating the field set twice.
function UserFormFields({ form, setForm, autoFocus }) {
  return (
    <Stack spacing={1.5}>
      <TextField
        label="Email *" size="small" fullWidth autoFocus={autoFocus}
        value={form.email}
        onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
      />
      <TextField
        label="Name" size="small" fullWidth
        value={form.name}
        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
      />
      <FormGroup row sx={{ alignItems: 'center', gap: 1 }}>
        {PRIVILEGE_FIELDS.map(({ key, label }) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                size="small"
                checked={!!form[key]}
                onChange={e => setForm(prev => ({ ...prev, [key]: e.target.checked }))}
              />
            }
            label={label}
          />
        ))}
        {ACCESS_FIELDS.map(({ key, label }) => (
          <Tooltip key={key} title={ACCESS_HINTS[key]}>
            <span>
              <EventAccessSelect label={label} value={form[key]} onChange={v => setForm(prev => ({ ...prev, [key]: v }))} />
            </span>
          </Tooltip>
        ))}
      </FormGroup>
    </Stack>
  );
}

export default function UserManagementSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminClient.getUsers());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const startEdit = (row) => {
    setEditingRow(row.rowIndex);
    setEditForm({
      email: row.email, name: row.name,
      judge: row.judge, accountAdmin: row.accountAdmin, eventAccess: row.eventAccess || '', tabulator: row.tabulator,
      workshopAccess: row.workshopAccess || '',
    });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditForm(EMPTY_FORM);
  };

  const saveEdit = async (rowIndex) => {
    if (!EMAIL_RE.test(editForm.email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await adminClient.updateUser(rowIndex, editForm);
      setRows(prev => prev.map(r => (r.rowIndex === rowIndex ? { ...editForm, email: editForm.email.trim().toLowerCase(), rowIndex } : r)));
      setEditingRow(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setAddForm(EMPTY_FORM);
    setAddError(null);
    setAddDialogOpen(true);
  };

  const handleAdd = async () => {
    if (!EMAIL_RE.test(addForm.email.trim())) {
      setAddError('Enter a valid email address.');
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await adminClient.addUser(addForm);
      setAddDialogOpen(false);
      await fetchRows();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await adminClient.deleteUser(deleteConfirm.rowIndex);
      setDeleteConfirm(null);
      await fetchRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openAdd}>
          Add User
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No users configured.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                {PRIVILEGE_COLUMNS.map(({ key, label }) => (
                  <TableCell key={key} sx={{ fontWeight: 600 }} align="center">
                    {ACCESS_HINTS[key] ? <Tooltip title={ACCESS_HINTS[key]}><span>{label}</span></Tooltip> : label}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600, width: 110 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const isEditing = editingRow === row.rowIndex;
                return (
                  <TableRow key={row.rowIndex} hover>
                    {isEditing ? (
                      <>
                        <TableCell>
                          <TextField
                            size="small" autoFocus fullWidth
                            value={editForm.email}
                            onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Escape') cancelEdit(); }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" fullWidth
                            value={editForm.name}
                            onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Escape') cancelEdit(); }}
                          />
                        </TableCell>
                        {PRIVILEGE_COLUMNS.map(({ key }) => (
                          <TableCell key={key} align="center">
                            {ACCESS_HINTS[key] ? (
                              <EventAccessSelect value={editForm[key]} onChange={v => setEditForm(prev => ({ ...prev, [key]: v }))} />
                            ) : (
                              <Checkbox
                                size="small"
                                checked={!!editForm[key]}
                                onChange={e => setEditForm(prev => ({ ...prev, [key]: e.target.checked }))}
                              />
                            )}
                          </TableCell>
                        ))}
                      </>
                    ) : (
                      <>
                        <TableCell><Typography variant="body2" fontWeight={500}>{row.email}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{row.name}</Typography></TableCell>
                        {PRIVILEGE_COLUMNS.map(({ key }) => (
                          <TableCell key={key} align="center">
                            {ACCESS_HINTS[key] ? (
                              <Typography variant="body2" color={row[key] ? 'text.primary' : 'text.disabled'}>{eventAccessLabel(row[key])}</Typography>
                            ) : (
                              <Checkbox size="small" checked={!!row[key]} disabled readOnly />
                            )}
                          </TableCell>
                        ))}
                      </>
                    )}
                    <TableCell>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title="Save">
                            <span>
                              <IconButton size="small" color="primary" onClick={() => saveEdit(row.rowIndex)} disabled={saving}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <span>
                              <IconButton size="small" onClick={cancelEdit} disabled={saving}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title="Edit user">
                            <IconButton size="small" onClick={() => startEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteConfirm(row)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add dialog */}
      <Dialog open={addDialogOpen} onClose={() => !adding && setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
            <UserFormFields form={addForm} setForm={setAddForm} autoFocus />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={adding}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={adding}>
            {adding ? <CircularProgress size={18} color="inherit" /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => !deleting && setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Remove <strong>{deleteConfirm?.email}</strong> from the user list? This cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This immediately revokes all of this person's portal access (Judge, Account Admin,
            Event Admin, Workshops, Tabulator) — not just from this list, on their very next request.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
